import { LoggingService } from "../../ServiceDesk/sdServices/loggingSvc";

/**
 * Result interface for outgoing request processing.
 */
export interface OutgoingRequestProcessingResult {
  /**
   * Should be a static name representing the name of the service being consumed.
   */
  dependencyName?: string;

  /**
   * Should be a static name (should not include dynamic content) that corresponds to the operation name for the dependency.
   * If possible, use the same operation names the dependency uses service side.
   */
  dependencyOperationName?: string;
}

/**
 * A telemetry processor for outgoing requests.
 */
export interface OutgoingRequestProcessor {
  /**
   * Indicates whether or not this process should process this request response. Processors should make sure they are not indicating that they
   * are capable of processing more general requests than they are specifically targeting.
   */
  canProcessRequestResponse(response: ng.IHttpPromiseCallbackArg<{}>): boolean;

  /**
   * Processes a request response, returning captured telemetry information.
   */
  processRequestResponse(response: ng.IHttpPromiseCallbackArg<{}>): OutgoingRequestProcessingResult;
}

/**
 * Service responsible for processing outgoing requests in a composable manner.
 */
export interface OutgoingRequestProcessingService {
  /**
   * Registers an outgoing request processor.
   * @param processor The processor to register to the service. Should be a defined and valid instance.
   */
  registerProcessor(processor: OutgoingRequestProcessor): void;
}

export let operationNameHeaderName = "ms-operationname";

export class OutgoingRequestProcessingServiceImplementation implements OutgoingRequestProcessingService {
  private processors: OutgoingRequestProcessor[] = [];

  // @ngInject
  constructor(private loggingService: LoggingService) {}

  public registerProcessor(processor: OutgoingRequestProcessor): void {
    if (!processor) {
      throw new Error("A valid processor must be passed into the register method.");
    }

    this.processors.push(processor);
  }

  public processRequestResponse(response: ng.IHttpPromiseCallbackArg<{}>): OutgoingRequestProcessingResult {
    for (let i = 0; i < this.processors.length; i++) {
      let currentProcessor = this.processors[i];
      try {
        let canProcess = currentProcessor.canProcessRequestResponse(response);
        if (canProcess) {
          let processingResult = currentProcessor.processRequestResponse(response);
          return processingResult;
        }
      } catch (error) {
        this.loggingService.logException(error);
      }
    }

    return undefined;
  }
}

export class DefaultServiceDeskProcessor implements OutgoingRequestProcessor {
  private isAbsolute = new RegExp("^([a-z]+://|//)", "i");
  private sdBaseUrl: string;
  constructor(baseUrl: string) {
    this.sdBaseUrl = baseUrl;
  }
  public canProcessRequestResponse(response: ng.IHttpPromiseCallbackArg<{}>): boolean {
    if (!response || !response.config || !response.config.url) {
      return false;
    }

    if (!this.isAbsolute.test(response.config.url)) {
      return true;
    }

    return response.config.url.indexOf(this.sdBaseUrl) == 0;
  }

  public processRequestResponse(response: ng.IHttpPromiseCallbackArg<{}>): OutgoingRequestProcessingResult {
    let urlToSplit = response.config.url;
    if (urlToSplit.indexOf(this.sdBaseUrl) == 0) {
      urlToSplit = urlToSplit.substr(this.sdBaseUrl.length);
    }

    let splitResult = urlToSplit.split("/");
    let operationName: string = undefined;
    if (response.headers && response.headers(operationNameHeaderName)) {
      operationName = response.headers(operationNameHeaderName);
    }

    return {
      dependencyName: `ServiceDesk.${splitResult[0]}`,
      dependencyOperationName: operationName
    };
  }
}
